/**
 * Buggino – cart drawer. Intercepts add-to-cart forms (form[data-ajax-cart]),
 * talks to routes.cart_add_url / routes.cart_url / routes.cart_change_url
 * (.js JSON endpoints) and re-renders the drawer lines client side.
 * Other scripts can trigger a refresh with
 * document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } })).
 */
(function () {
  'use strict';

  const drawer = document.querySelector('[data-cart-drawer]');
  const formatMoney = (cents, format) =>
    window.Buggino && window.Buggino.formatMoney
      ? window.Buggino.formatMoney(cents, format)
      : (cents / 100).toFixed(2).replace('.', ',') + ' €';

  const config = {
    cartUrl: drawer ? drawer.dataset.cartUrl : '/cart',
    addUrl: drawer ? drawer.dataset.cartAddUrl : '/cart/add',
    changeUrl: drawer ? drawer.dataset.cartChangeUrl : '/cart/change',
    moneyFormat: drawer ? drawer.dataset.moneyFormat : '',
    threshold: drawer ? Number(drawer.dataset.threshold || 0) : 0,
    shippingRatesEnabled: drawer ? drawer.dataset.shippingRatesEnabled === 'true' : false,
  };

  const i18n = (name) => {
    const el = drawer && drawer.querySelector(`[data-i18n-${name}]`);
    return el ? el.textContent : '';
  };

  function jsonHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
  }

  async function fetchCart() {
    const response = await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart');
    return response.json();
  }

  let lineChangeQueue = Promise.resolve();
  let renderedCart = null;
  let renderedLineTargets = new Map();
  const removedLineTargets = new WeakSet();
  const pendingLineTargets = new Map();

  function canonicalValue(value) {
    if (Array.isArray(value)) return ['array', value.map(canonicalValue)];
    if (value && typeof value === 'object') {
      return ['object', Object.keys(value).sort().map((key) => [key, canonicalValue(value[key])])];
    }
    return value;
  }

  function lineIdentity(item) {
    if (typeof item.variant_id === 'number' && !Number.isSafeInteger(item.variant_id)) return null;
    const variant = typeof item.variant_id === 'string' || typeof item.variant_id === 'number' ? String(item.variant_id) : '';
    if (!/^[1-9]\d*$/.test(variant)) return null;
    const properties = item.properties == null ? {} : item.properties;
    if (typeof properties !== 'object' || Array.isArray(properties)) return null;
    let plan = null;
    if (item.selling_plan_allocation != null) {
      const allocation = item.selling_plan_allocation;
      if (!allocation.selling_plan || allocation.selling_plan.id == null) return null;
      const id = allocation.selling_plan.id;
      if (typeof id === 'number' && !Number.isSafeInteger(id)) return null;
      plan = typeof id === 'string' || typeof id === 'number' ? String(id) : '';
      if (!/^[1-9]\d*$/.test(plan)) return null;
    }
    return JSON.stringify([variant, canonicalValue(properties), plan]);
  }

  function hasLineRelationships(item) {
    return Boolean(item.parent_relationship) ||
      (item.item_components != null && (!Array.isArray(item.item_components) || item.item_components.length > 0));
  }

  function captureLineTarget(key) {
    if (renderedLineTargets.has(key)) return renderedLineTargets.get(key);
    const items = renderedCart ? renderedCart.items : [];
    const exact = items.filter((item) => item.key === key);
    if (exact.length !== 1) return null;
    const identity = lineIdentity(exact[0]);
    if (identity === null) return null;
    const target = {
      key,
      identity,
      allowRekey: !hasLineRelationships(exact[0]) && items.filter((item) => lineIdentity(item) === identity).length === 1,
    };
    renderedLineTargets.set(key, target);
    return target;
  }

  function changeLine(key, quantity) {
    // Capture the clicked line before a preceding queued response replaces it.
    const target = captureLineTarget(key);
    if (target) pendingLineTargets.set(target, (pendingLineTargets.get(target) || 0) + 1);
    const request = lineChangeQueue.then(async () => {
      if (!target) throw new Error('line');
      const cart = await fetchCart();
      if (removedLineTargets.has(target)) {
        if (quantity === 0) return cart;
        throw new Error('line');
      }
      const items = cart.items || [];
      const exact = items.filter((item) => item.key === target.key);
      let item = exact.length === 1 && lineIdentity(exact[0]) === target.identity ? exact[0] : null;
      if (!item) {
        // A repricing may change keys, but never guess by line position/variant.
        if (exact.length) throw new Error('line');
        const matches = items.filter((candidate) => lineIdentity(candidate) === target.identity);
        if (matches.length === 0 && quantity === 0) {
          removedLineTargets.add(target);
          return cart;
        }
        if (!target.allowRekey || matches.length !== 1 || hasLineRelationships(matches[0])) throw new Error('line');
        item = matches[0];
      }
      const response = await fetch(`${config.changeUrl}.js`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ id: item.key, quantity }),
      });
      if (!response.ok) throw new Error('change');
      const updatedCart = await response.json();
      if (quantity === 0) removedLineTargets.add(target);
      return updatedCart;
    });
    // Keep responses in order, and let later changes run after a failed one.
    lineChangeQueue = request.catch(() => {});
    const releaseTarget = () => {
      if (!target) return;
      const remaining = pendingLineTargets.get(target) - 1;
      if (remaining > 0) pendingLineTargets.set(target, remaining);
      else pendingLineTargets.delete(target);
    };
    request.then(releaseTarget, releaseTarget);
    return request;
  }

  function imageUrl(url, width) {
    if (!url) return '';
    return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  }

  function collectDiscounts(cart) {
    const totals = new Map();
    const applicationTitles = new Set();

    (cart.cart_level_discount_applications || []).forEach((application) => {
      const amount = Number(application.total_allocated_amount || 0);
      if (!application.title || amount <= 0) return;
      applicationTitles.add(application.title);
      totals.set(application.title, (totals.get(application.title) || 0) + amount);
    });

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        const amount = Number(discount.amount || 0);
        if (!discount.title || amount <= 0 || applicationTitles.has(discount.title)) return;
        totals.set(discount.title, (totals.get(discount.title) || 0) + amount);
      });
    });

    return Array.from(totals, ([title, amount]) => ({ title, amount }));
  }

  function renderLines(cart) {
    const body = drawer.querySelector('[data-cart-drawer-body]');
    const template = drawer.querySelector('[data-cart-line-template]');
    const emptyTemplate = drawer.querySelector('[data-cart-empty-template]');
    const status = body.querySelector('[data-cart-status]');

    Array.from(body.children).forEach((child) => {
      if (child !== status) child.remove();
    });

    if (!cart.items.length) {
      body.appendChild(emptyTemplate.content.cloneNode(true));
      return;
    }

    const list = document.createElement('ul');
    list.className = 'cart-lines';
    list.setAttribute('data-cart-lines', '');
    const summaryDiscountTitles = new Set(collectDiscounts(cart).map((discount) => discount.title));

    cart.items.forEach((item, index) => {
      const line = index + 1;
      const node = template.content.cloneNode(true);
      const root = node.querySelector('[data-cart-line]');
      root.dataset.line = String(line);
      root.dataset.key = item.key;

      const imageLink = node.querySelector('.cart-line__image');
      const img = imageLink.querySelector('img');
      imageLink.href = item.url;
      if (item.image) {
        img.src = imageUrl(item.image, 180);
        img.alt = item.product_title;
      } else {
        img.remove();
      }

      const title = node.querySelector('.cart-line__title');
      title.href = item.url;
      title.textContent = item.product_title;

      const variant = node.querySelector('.cart-line__variant');
      const details = [];
      if (!item.product_has_only_default_variant && item.variant_title) details.push(item.variant_title);
      Object.keys(item.properties || {}).forEach((key) => {
        if (key.charAt(0) !== '_' && item.properties[key]) details.push(`${key}: ${item.properties[key]}`);
      });
      if (details.length) variant.textContent = details.join(' · ');
      else variant.remove();

      const discountList = node.querySelector('[data-cart-line-discounts]');
      (item.discounts || [])
        .filter((discount) => discount.title && discount.amount > 0 && !summaryDiscountTitles.has(discount.title))
        .forEach((discount) => {
          const entry = document.createElement('li');
          entry.textContent = `${discount.title} (−${formatMoney(discount.amount, config.moneyFormat)})`;
          discountList.appendChild(entry);
        });
      discountList.toggleAttribute('hidden', discountList.childElementCount === 0);

      const input = node.querySelector('[data-qty-input]');
      input.value = String(item.quantity);
      input.id = `qty-${item.key}`;
      input.dataset.line = String(line);

      const price = node.querySelector('.cart-line__price');
      if (item.original_line_price !== item.final_line_price) {
        const compare = document.createElement('s');
        compare.className = 'price__compare';
        compare.textContent = formatMoney(item.original_line_price, config.moneyFormat);
        price.appendChild(compare);
        price.appendChild(document.createTextNode(' '));
      }
      price.appendChild(document.createTextNode(formatMoney(item.final_line_price, config.moneyFormat)));

      const remove = node.querySelector('[data-remove-line]');
      remove.href = `${config.changeUrl}?line=${line}&quantity=0`;
      remove.dataset.line = String(line);

      list.appendChild(node);
    });

    body.appendChild(list);
  }

  function renderSummary(cart) {
    const foot = drawer.querySelector('[data-cart-drawer-foot]');
    const count = drawer.querySelector('[data-cart-drawer-count]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const discounts = drawer.querySelector('[data-cart-discounts]');
    const shippingNote = drawer.querySelector('[data-cart-shipping-note]');
    if (count) count.textContent = `(${cart.item_count})`;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price, config.moneyFormat);
    if (discounts) {
      discounts.replaceChildren();
      collectDiscounts(cart).forEach((discount) => {
        const row = document.createElement('div');
        row.className = 'cart-summary__row cart-line__discounts';
        const title = document.createElement('span');
        title.textContent = discount.title;
        const value = document.createElement('span');
        value.textContent = `−${formatMoney(discount.amount, config.moneyFormat)}`;
        row.append(title, value);
        discounts.appendChild(row);
      });
      discounts.toggleAttribute('hidden', discounts.childElementCount === 0);
    }
    if (foot) foot.toggleAttribute('hidden', cart.item_count === 0);

    // Admin thresholds are EUR amounts, never unconverted presentment amounts.
    const shippingRatesEnabled = config.shippingRatesEnabled && cart.currency === 'EUR' &&
      Number.isFinite(config.threshold) && config.threshold > 0;
    const freeShippingReached = shippingRatesEnabled && cart.total_price >= config.threshold;
    if (shippingNote) {
      shippingNote.innerHTML = shippingRatesEnabled
        ? (freeShippingReached ? shippingNote.dataset.freeNote : shippingNote.dataset.paidNote)
        : (shippingNote.dataset.neutralNote || '');
    }

    const bar = drawer.querySelector('[data-shipping-bar]');
    if (bar) bar.hidden = !shippingRatesEnabled;
    if (bar && shippingRatesEnabled) {
      const text = bar.querySelector('[data-shipping-text]');
      const fill = bar.querySelector('[data-shipping-fill]');
      const reached = cart.total_price >= config.threshold;
      bar.classList.toggle('shipping-bar--reached', reached);
      if (text) {
        if (reached) {
          text.textContent = i18n('free');
        } else {
          const remaining = formatMoney(config.threshold - cart.total_price, config.moneyFormat);
          text.innerHTML = i18n('remaining').replace('[amount]', remaining);
        }
      }
      if (fill) {
        const progress = Math.min(100, Math.round((cart.total_price / config.threshold) * 100));
        fill.style.setProperty('--progress', `${progress}%`);
      }
    }

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
    });
  }

  function render(cart) {
    if (!drawer) return;
    renderedCart = cart;
    renderedLineTargets = new Map();
    // Preserve outstanding row instances across an earlier queued rerender.
    // A consumed instance must never be attached to an identical replacement.
    const identities = cart.items.map(lineIdentity);
    cart.items.forEach((item, index) => {
      const identity = identities[index];
      if (identity === null) return;
      const matches = Array.from(pendingLineTargets.keys()).filter((target) =>
        !removedLineTargets.has(target) && target.identity === identity &&
        (target.key === item.key || (target.allowRekey && !hasLineRelationships(item) && identities.filter((value) => value === identity).length === 1))
      );
      if (matches.length) renderedLineTargets.set(item.key, matches.length === 1 ? matches[0] : null);
    });
    renderLines(cart);
    renderSummary(cart);
    document.dispatchEvent(new CustomEvent('cart:rendered', { detail: cart }));
  }

  function open() {
    if (!drawer) return;
    window.Buggino && window.Buggino.openDialog ? window.Buggino.openDialog(drawer) : drawer.showModal();
  }

  function close() {
    if (!drawer) return;
    window.Buggino && window.Buggino.closeDialog ? window.Buggino.closeDialog(drawer) : drawer.close();
  }

  function setStatus(message) {
    const status = drawer && drawer.querySelector('[data-cart-status]');
    if (status) {
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = message;
      }, 50);
    }
  }

  async function refresh(shouldOpen) {
    try {
      const cart = await fetchCart();
      render(cart);
      if (shouldOpen) open();
      return cart;
    } catch (error) {
      return null;
    }
  }

  const pendingAddForms = new WeakSet();

  async function handleAddSubmit(event) {
    const form = event.target;
    if (!form.matches('form[data-ajax-cart]')) return;
    if (!drawer) return; // No drawer: let the browser post to /cart/add normally.
    event.preventDefault();
    if (pendingAddForms.has(form)) return;

    const button = form.querySelector('[type="submit"]');
    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) errorBox.setAttribute('hidden', '');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    }

    let addConfirmed = false;
    pendingAddForms.add(form);
    try {
      const formData = new FormData(form);
      const response = await fetch(`${config.addUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || i18n('error'));
      }
      addConfirmed = true;
      const cart = await refresh(true);
      setStatus(i18n('added'));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      if (!cart) window.location.assign(config.cartUrl);
    } catch (error) {
      if (!addConfirmed && errorBox) {
        errorBox.textContent = error.message || i18n('error');
        errorBox.removeAttribute('hidden');
      }
    } finally {
      pendingAddForms.delete(form);
      if (button) {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }

  async function handleDrawerChange(event) {
    const input = event.target.closest('[data-qty-input]');
    if (!input || !drawer.contains(input)) return;
    const row = input.closest('[data-cart-line]');
    const key = row && row.dataset.key;
    if (!key) return;
    const body = drawer.querySelector('[data-cart-drawer-body]');
    body.setAttribute('aria-busy', 'true');
    body.classList.add('is-loading');
    try {
      const cart = await changeLine(key, Math.max(0, Number(input.value) || 0));
      render(cart);
      setStatus(i18n('updated'));
    } catch (error) {
      setStatus(i18n('error'));
      await refresh(false);
    } finally {
      body.removeAttribute('aria-busy');
      body.classList.remove('is-loading');
    }
  }

  async function handleDrawerClick(event) {
    const remove = event.target.closest('[data-remove-line]');
    if (remove && drawer.contains(remove)) {
      event.preventDefault();
      const row = remove.closest('[data-cart-line]');
      const key = row && row.dataset.key;
      if (!key) return;
      try {
        const cart = await changeLine(key, 0);
        render(cart);
        setStatus(i18n('updated'));
      } catch (error) {
        setStatus(i18n('error'));
        await refresh(false);
      }
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      close();
    }
  }

  function init() {
    document.addEventListener('submit', handleAddSubmit);

    if (!drawer) return;

    document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        refresh(true);
      });
    });

    drawer.addEventListener('change', handleDrawerChange);
    drawer.addEventListener('click', handleDrawerClick);
    if (window.Buggino && window.Buggino.bindBackdropClose) window.Buggino.bindBackdropClose(drawer);

    document.addEventListener('cart:refresh', (event) => {
      const shouldOpen = !event.detail || event.detail.open !== false;
      refresh(shouldOpen);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
